import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import axios from "axios";
import { storage, auth } from "../firebase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Authorized case-file download. The backend verifies the caller is the case owner,
// the selected designer, or an admin, then returns a short-lived signed URL. Non-
// authorized users get a 403 (thrown). Triggers a browser download on success.
export async function downloadCaseFile(caseId, refOrUrl, name) {
  const token = await auth.currentUser.getIdToken();
  const { data } = await axios.post(
    `${API}/cases/${caseId}/download-url`,
    { ref: refOrUrl, name: name || "" },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const a = document.createElement("a");
  a.href = data.url;
  a.target = "_blank";
  a.rel = "noreferrer";
  if (name) a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function contentTypeFor(file) {
  // Browsers often report an empty MIME type for .stl; force it so Storage rules validate.
  if (/\.stl$/i.test(file.name)) return "model/stl";
  return file.type || "application/octet-stream";
}

function storagePath(file, folder) {
  return `${folder}/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
}

export async function uploadFile(file, folder) {
  const path = storagePath(file, folder);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: contentTypeFor(file) });
  const url = await getDownloadURL(storageRef);
  return { url, name: file.name, path };
}

export async function uploadMany(files, folder) {
  const results = [];
  for (const file of files) {
    results.push(await uploadFile(file, folder));
  }
  return results;
}

// Resumable upload with progress reporting. Ideal for large STL files.
// onProgress receives (percent:number 0-100, snapshot). Returns a Promise
// that resolves to { url, name, path } or rejects on error (retry by calling again).
export function uploadFileResumable(file, folder, onProgress) {
  const path = storagePath(file, folder);
  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, file, { contentType: contentTypeFor(file) });
  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        const pct = snap.totalBytes
          ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
          : 0;
        onProgress && onProgress(pct, snap);
      },
      (err) => reject(err),
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, name: file.name, path });
      }
    );
  });
}
